import { Box, Toolbar } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, useOutletContext, useParams } from 'react-router';
import VerificationHeader from '../../components/verification/VerificationHeader';
import LinkUnavailable from '../../components/verification/LinkUnavailable';
import { setEnableDocUpload, setUserDetails } from '../../store/ui/actions';
import { getUserDetails } from '../../store/ui/selector';
import { ApplicantionFromToken } from '../../models/doc-upload.model';
import { useEffect, useState } from 'react';
import { getApplicantFromToken } from '../../services/plaid.service';
import { FullStory } from '@fullstory/browser';
import { useVerificationNav } from '../../utils/useVerificationNav';
import { setApplicantToken, tokenFromSearch } from '../../utils/applicantToken';
import { LinkProblem, onLinkProblem } from '../../utils/linkProblem';
import { toUserDetails } from '../../utils/applicantDetails';

type ContextType = { userDetails: ApplicantionFromToken | null; handleNext: VoidFunction; handleBack: VoidFunction };

export default function IncomeVerification() {
  const dispatch = useDispatch();
  const { propertyCode } = useParams();
  const { search } = useLocation();
  const { currentPage, isGuarantor, goNext, goBack } = useVerificationNav();
  const userDetails = useSelector(getUserDetails);
  const [linkProblem, setLinkProblem] = useState<LinkProblem | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);

  const token = tokenFromSearch(search);
  // During render, not in an effect: child effects run before this component's, and every
  // applicant API call reads the token from here (memory only — see applicantToken.ts).
  setApplicantToken(token);

  // Any applicant call answering "link invalid / expired / application closed" lands here.
  // Declared before the fetch below so it is subscribed by the time that request goes out.
  useEffect(() => onLinkProblem(setLinkProblem), []);

  useEffect(() => {
    setLinkProblem(null);
    setLoadFailed(false);
    if (!token) return;
    let cancelled = false;
    getApplicantFromToken(token).then(user => {
      if (cancelled) return;
      // Trimmed to the fields the app reads before it reaches the (persisted) store: the API
      // live during the rollout sends the whole application, co-applicants' details included.
      const uD = toUserDetails(user?.data);
      if (!uD) {
        // A dead link has already been reported through onLinkProblem; anything else
        // (network, 5xx) is a load failure the user can retry.
        setLoadFailed(true);
        return;
      }
      dispatch(setUserDetails(uD));
      dispatch(setEnableDocUpload(!!uD.enable_doc_upload));
      FullStory('setIdentity', { uid: uD.applicant_id });
      FullStory('setProperties', {
        type: 'user',
        properties: { propertyCode, propertyName: uD.propertyName },
      });
    });
    return () => {
      cancelled = true;
    };
  }, [token, propertyCode, dispatch, loadAttempt]);

  const showLogo = currentPage === 'account' || currentPage === 'intro' || currentPage === 'types' || (currentPage === 'cosign' && isGuarantor);

  // No token at all is the same dead end as a refused one.
  const problem: LinkProblem | null = token ? linkProblem : 'link_invalid';
  // Details already loaded for this same link (persisted across a refresh) are enough to carry
  // on; without them the flow would render an empty form with Continue disabled.
  const showLoadFailed = !problem && loadFailed && !userDetails;

  const renderBody = () => {
    if (problem) return <LinkUnavailable reason={problem} />;
    if (showLoadFailed) return <LinkUnavailable reason="load_failed" onRetry={() => setLoadAttempt(n => n + 1)} />;
    return <Outlet context={{ userDetails, handleNext: goNext, handleBack: goBack }} />;
  };

  const headerWithLogo = showLogo || !!problem || showLoadFailed;

  return (
    <>
      <VerificationHeader showLogo={headerWithLogo} />
      <Toolbar sx={{ display: { xs: headerWithLogo ? 'flex' : 'none', md: 'flex' } }} />
      <Box sx={{ color: 'grey.900', pt: { xs: headerWithLogo ? 5.5 : 0, md: 5.5 }, pb: 4 }}>{renderBody()}</Box>
    </>
  );
}

export function useOptions() {
  return useOutletContext<ContextType>();
}
